import { useState, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import VehicleImage from "@components/ui/VehicleImage";
import Avvvatars from "avvvatars-react";
import { navigateTo } from "context/navigation.service";
import type { ClientVehicle } from "types/client_vehicle";
import type { DossierTimelineEvent, DossierOrdreSummary } from "types/dossier";

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" },
  { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" },
  { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" },
  { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

function formatEventDate(iso: string): { text: string; color?: string } {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  if (eventDay.getTime() === today.getTime()) return { text: `Auj. ${time}`, color: "#10b981" };
  if (eventDay.getTime() === yesterday.getTime()) return { text: `Hier ${time}`, color: "#171717" };
  return { text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + time };
}

function TimelineSidebar({ vehicleId, timeline, ordres, onClose }: {
  vehicleId: string;
  timeline: DossierTimelineEvent[];
  ordres: DossierOrdreSummary[];
  onClose: () => void;
}) {
  const { sorted, vehicleOrdres } = useMemo(() => {
    const vehicleOrdreNums = new Set(
      ordres.filter((o) => o.vehicle_id === vehicleId).map((o) => o.num)
    );
    const events = timeline.filter((ev) =>
      ev.vehicle_id === vehicleId ||
      (ev.ordre_ref && vehicleOrdreNums.has(ev.ordre_ref))
    );
    const sorted = [...events].sort((a, b) => (a.date < b.date ? 1 : -1));
    const vehicleOrdres = ordres.filter((o) => o.vehicle_id === vehicleId);
    return { sorted, vehicleOrdres };
  }, [vehicleId, timeline, ordres]);

  return (
    <div className="w-[340px] flex-shrink-0 border-l border-neutral-200 flex flex-col">
      <div className="px-4 py-3 border-b border-neutral-100">
        <span className="text-h3 font-semibold text-neutral-600">Historique</span>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ maxHeight: "65vh" }}>
        {/* Linked ordres */}
        {vehicleOrdres.length > 0 && (
          <div className="flex flex-col gap-1 mb-4">
            <span className="text-h4 font-medium text-neutral-500 mb-1">Ordres ({vehicleOrdres.length})</span>
            {vehicleOrdres.map((o) => (
              <div
                key={o.id}
                className="flex flex-row items-center gap-2 py-1.5 cursor-pointer hover:bg-neutral-50 rounded px-2"
                onClick={() => { onClose(); navigateTo({ route: `/ordre/${o.id}` }); }}
              >
                <span
                  className="text-h4 font-medium px-2 py-0.5 rounded-full"
                  style={{ color: o.status_color, background: o.status_color + "18" }}
                >
                  {o.status}
                </span>
                <span className="text-h4 font-medium text-neutral-700">{o.num}</span>
                {o.type && <span className="text-h4 text-neutral-400 truncate">{o.type}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Timeline events */}
        {sorted.length > 0 && (
          <div className="flex flex-col">
            <span className="text-h4 font-medium text-neutral-500 mb-2">Chronologie ({sorted.length})</span>
            {sorted.map((ev, i) => {
              const dt = formatEventDate(ev.date);
              return (
                <div key={ev.id} className="flex flex-row">
                  <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
                    <div className="rounded-full mt-1.5 flex-shrink-0" style={{ width: 8, height: 8, background: "#A3A3A3" }} />
                    {i < sorted.length - 1 && <div className="w-0.5 bg-neutral-200 flex-1 my-1" />}
                  </div>
                  <div className="flex flex-col ml-2 flex-1 pb-4 min-w-0">
                    <div className="flex flex-row items-center gap-2">
                      <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
                        <Avvvatars value={ev.user_name} style="shape" size={22} />
                      </div>
                      <span className="text-h4 font-medium text-neutral-800 truncate">{ev.user_name}</span>
                      <span className="text-h5 ml-auto flex-shrink-0" style={{ color: dt.color || "#a3a3a3" }}>{dt.text}</span>
                    </div>
                    <span className="text-h4 text-neutral-500 mt-0.5 ml-[30px]">{ev.action}</span>
                    {ev.type === "status_change" && ev.status_label && (
                      <span className="text-h4 font-medium mt-0.5 ml-[30px]" style={{ color: ev.status_type === "success" ? "#10b981" : ev.status_type === "warning" ? "#f59e0b" : "#525252" }}>
                        {ev.status_label}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {sorted.length === 0 && vehicleOrdres.length === 0 && (
          <div className="text-h4 text-center text-neutral-400 py-8">Aucun historique</div>
        )}
      </div>
    </div>
  );
}

export interface VehicleEditModalProps {
  vehicle: ClientVehicle;
  onSave: (data: Partial<ClientVehicle>) => void;
  onClose: () => void;
  saving?: boolean;
  timeline?: DossierTimelineEvent[];
  ordres?: DossierOrdreSummary[];
}

export default function VehicleEditModal({ vehicle, onSave, onClose, saving, timeline, ordres }: VehicleEditModalProps) {
  const [plate, setPlate] = useState(vehicle.plate || "");
  const [marque, setMarque] = useState(vehicle.marque || "");
  const [modele, setModele] = useState(vehicle.modele || "");
  const [coloris, setColoris] = useState(vehicle.coloris || "");
  const [km, setKm] = useState(vehicle.km != null ? String(vehicle.km) : "");
  const [vin, setVin] = useState(vehicle.vin || "");
  const [mec, setMec] = useState(vehicle.mec || null);

  const hasTimeline = timeline && ordres && (timeline.length > 0 || ordres.length > 0);

  return (
    <BaseModal onClose={onClose} title={plate || "Véhicule"} width={hasTimeline ? "max-w-4xl" : "max-w-lg"} startIcon={<Icon1 name="car" size={20} />}>
      <div className="flex flex-row">
        {/* Left — form */}
        <div className="flex flex-col gap-4 p-4 flex-1 min-w-0">
          <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ height: 160 }}>
            <VehicleImage marque={marque} modele={modele} coloris={coloris} mec={mec || undefined} alt={plate} className="w-full h-full" />
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Immatriculation" type="plate" value={plate} onChange={setPlate} />
            </div>
            <div className="flex-1">
              <DropDown label="Coloris" options={COLORIS_OPTIONS} value={coloris} onSelect={(v) => setColoris(String(v))} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Marque" value={marque} onChange={setMarque} />
            </div>
            <div className="flex-1">
              <TextField label="Modèle" value={modele} onChange={setModele} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Km" type="number" value={km} onChange={setKm} />
            </div>
            <div className="flex-1">
              <DatePicker label="MEC" seconds={mec} onChange={setMec} />
            </div>
          </div>
          <TextField label="VIN" value={vin} onChange={setVin} />
          <div className="flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
            <Button
              variant="small"
              label={saving ? "Enregistrement..." : "Enregistrer"}
              onClick={() => onSave({ plate, marque, modele, coloris: coloris as ClientVehicle["coloris"], km: km === "" ? undefined : Number(km), vin, mec: mec || undefined })}
              disabled={saving}
            />
          </div>
        </div>

        {/* Right — timeline */}
        {hasTimeline && (
          <TimelineSidebar vehicleId={vehicle.id} timeline={timeline!} ordres={ordres!} onClose={onClose} />
        )}
      </div>
    </BaseModal>
  );
}
